/**
 * Weekly quest card (specs/home-page Story 4, AC-22 revised): the week's three tasks on a pitch, the
 * countdown to the week's end, and the chest bar. Rewards are auto-granted, so there is nothing to claim
 * here; the celebration lives in ChestCelebrationModal. A guest's chest is never granted (nothing is
 * banked for a guest), so for a guest the bar offers sign-up instead (AC-24).
 */

import React, { useCallback, useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { Image } from 'expo-image';
import { ChevronRight, Lock } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { ASSET_PATHS, IDLE_COIN_SOURCE, getAssetUrl } from '../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import {
  HOME_CHEST_LOCK_KEY,
  HOME_COPY,
  HOME_QUESTS_PER_WEEK,
  HOME_TEST_IDS,
} from '../../../src/home/constants/home.constants';
import { formatWeekCountdown } from '../../../src/home/helpers/homeCountdown.helper';
import type { WeeklyQuestsView, WeeklyQuestTaskView } from '../../../src/home/types/home.types';
import { HomeRegionError } from '../HomeRegionState/HomeRegionError';
import { HomeRegionSkeleton } from '../HomeRegionState/HomeRegionSkeleton';
import { QuestTrackNode, type QuestNodeState } from './QuestTrackNode';
import { WeeklyPitchBackground } from './WeeklyPitchBackground';
import { QUEST_CHEST_ARROW_SIZE, getWeeklyQuestCardStyles } from './WeeklyQuestCard.styles';

const CHEST_ICON_SOURCE = { uri: getAssetUrl(ASSET_PATHS.REWARD_ICON) };
const numberFormat = new Intl.NumberFormat();

export interface WeeklyQuestCardProps {
  quests: WeeklyQuestsView | null;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /** A guest tapped a task they cannot do, or the locked chest (`HOME_CHEST_LOCK_KEY`): the container opens the sign-up path (AC-24). */
  onLockedTaskPress: (taskKey: string) => void;
  nowMs?: number;
}

/** done → the first open task is "active" → the rest "pending" (AC-22). */
export function questNodeStates(tasks: WeeklyQuestTaskView[]): QuestNodeState[] {
  let activeAssigned = false;
  return tasks.map((task) => {
    if (task.completedAt) return 'done';
    if (!activeAssigned) {
      activeAssigned = true;
      return 'active';
    }
    return 'pending';
  });
}

export const WeeklyQuestCard: React.FC<WeeklyQuestCardProps> = ({
  quests,
  isLoading,
  isError,
  onRetry,
  onLockedTaskPress,
  nowMs,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getWeeklyQuestCardStyles(theme), [theme]);
  const now = nowMs ?? Date.now();
  const handleLockedChestPress = useCallback(() => onLockedTaskPress(HOME_CHEST_LOCK_KEY), [onLockedTaskPress]);

  const renderChestBar = (view: WeeklyQuestsView) => {
    if (view.chest.guestLocked) {
      return (
        <Pressable
          style={({ pressed, hovered }) => [
            styles.chestBar,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.rowPressed,
            hovered && Platform.OS === 'web' && styles.rowHover,
          ]}
          onPress={handleLockedChestPress}
          accessibilityRole="button"
          accessibilityLabel={HOME_COPY.WEEKLY_CHEST_GUEST}
          testID={`${HOME_TEST_IDS.weeklyChestRow}-locked`}
        >
          <Lock size={QUEST_CHEST_ARROW_SIZE} color={theme.gray[600]} strokeWidth={2.4} />
          <Text style={styles.chestText}>{HOME_COPY.WEEKLY_CHEST_GUEST}</Text>
        </Pressable>
      );
    }
    const chestOpened = view.chest.grantedAt != null;
    return (
      <View style={styles.chestBar} testID={HOME_TEST_IDS.weeklyChestRow}>
        <Image source={CHEST_ICON_SOURCE} style={styles.chestIcon} contentFit="contain" alt={WEB_IMAGE_ALT_DECORATIVE} />
        {chestOpened ? (
          <Text style={[styles.chestText, styles.chestOpened]}>{HOME_COPY.WEEKLY_CHEST_OPENED}</Text>
        ) : (
          <>
            <Text style={styles.chestText}>{HOME_COPY.WEEKLY_CHEST_FINISH}</Text>
            <View style={styles.chestReward}>
              <ChevronRight size={QUEST_CHEST_ARROW_SIZE} color={theme.accent} strokeWidth={3} />
              <Image source={IDLE_COIN_SOURCE} style={styles.coin} contentFit="contain" alt={WEB_IMAGE_ALT_DECORATIVE} />
              <Text style={styles.chestRewardText}>{numberFormat.format(view.chest.rewardCoins)}</Text>
            </View>
          </>
        )}
      </View>
    );
  };

  const renderBody = () => {
    if (isLoading) return <HomeRegionSkeleton rows={HOME_QUESTS_PER_WEEK} testID={HOME_TEST_IDS.weeklySkeleton} />;
    if (isError || !quests) return <HomeRegionError onRetry={onRetry} testID={`${HOME_TEST_IDS.regionError}-weekly`} />;

    const states = questNodeStates(quests.tasks);

    return (
      <>
        <View style={styles.tasks}>
          {quests.tasks.map((task, index) => (
            <QuestTrackNode
              key={task.key}
              task={task}
              state={states[index]}
              locked={task.guestLocked}
              onLockedPress={onLockedTaskPress}
              styles={styles}
              theme={theme}
            />
          ))}
        </View>
        {renderChestBar(quests)}
      </>
    );
  };

  return (
    <View style={styles.card} testID={HOME_TEST_IDS.weeklyCard}>
      <WeeklyPitchBackground style={styles.pitch} testID={HOME_TEST_IDS.weeklyPitch} />
      <View style={styles.headerRow}>
        <View style={styles.headerText}>
          <Text style={styles.label}>{HOME_COPY.WEEKLY_LABEL}</Text>
        </View>
        {quests && !isError && (
          <Text style={styles.countdown} testID={HOME_TEST_IDS.weeklyCountdown}>
            {formatWeekCountdown(quests.endsAt, now)}
          </Text>
        )}
      </View>
      {renderBody()}
    </View>
  );
};
